// src/pages/theater_staff/StaffProfile.jsx
import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Avatar, Row, Col, Typography, Divider, Spin, Descriptions } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, BankOutlined, LockOutlined, HomeOutlined, EnvironmentOutlined } from '@ant-design/icons';
import PageHeading from '../../components/PageHeading';
import axiosInstance from '../../utils/axios';

const { Title, Text } = Typography;

const StaffProfile = () => {
  const [theatreData, setTheatreData] = useState(null);
  const [staffData, setStaffData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('theatre-management/profile/');
      
      // Response is an array with one theatre object
      const theatre = response.data[0];
      const staff = theatre.users[0]; // Current logged-in staff

      setTheatreData(theatre);
      setStaffData(staff);

      // Pre-fill form with staff editable fields
      form.setFieldsValue({
        name: staff.name || '',
        email: staff.email || '',
        mobile: staff.mobile || '',
      });
    } catch (error) {
      message.error('Unable to load your profile. Please try again.');
      console.error('Profile fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (values) => {
    setSaving(true);
    try {
      const payload = {
        name: values.name,
        email: values.email,
        mobile: values.mobile || null,
      };

      if (values.password) {
        payload.password = values.password;
      }

      // Update staff profile (use staff ID from response)
      await axiosInstance.patch(`theatre-management/staff/${staffData.id}/`, payload);
      
      message.success('Profile updated successfully!');
      fetchProfile(); // Refresh to get updated data
    } catch (error) {
      const errMsg = error.response?.data?.message || error.response?.data?.detail || 'Update failed';
      message.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading your profile...</div>
      </div>
    );
  }

  if (!theatreData || !staffData) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Text type="danger">Profile information not found</Text>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <PageHeading name="My Profile" icon={<UserOutlined />} />

      <Row justify="center" style={{ marginTop: 32 }}>
        <Col xs={24} sm={22} md={18} lg={16} xl={14}>
          <Card>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <Avatar size={100} icon={<UserOutlined />} style={{ backgroundColor: '#6366f1' }} />
              <Title level={3} style={{ margin: '16px 0 8px' }}>
                {staffData.name}
              </Title>
              <Text type="secondary">Theater Staff Member</Text>
            </div>

            <Divider>My Theatre</Divider>

            {/* Theatre Information - Read Only */}
            <Descriptions bordered column={1} style={{ marginBottom: 32 }}>
              <Descriptions.Item label={<><BankOutlined /> Theatre Name</>}>
                <strong>{theatreData.name}</strong>
              </Descriptions.Item>
              <Descriptions.Item label={<><HomeOutlined /> Owner</>}>
                {theatreData.owner}
              </Descriptions.Item>
              <Descriptions.Item label={<><EnvironmentOutlined /> Address</>}>
                {theatreData.address}
                <br />
                {theatreData.district?.name}, {theatreData.state?.name}
                <br />
                PIN: {theatreData.postal_code}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {theatreData.phone_number}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {theatreData.email}
              </Descriptions.Item>
              <Descriptions.Item label="Facilities">
                {theatreData.facilities || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Total Screens">
                {theatreData.total_screens}
              </Descriptions.Item>
            </Descriptions>

            <Divider>My Details</Divider>

            {/* Editable Staff Fields */}
            <Form form={form} layout="vertical" onFinish={handleUpdate}>
              <Form.Item
                name="name"
                label="Full Name"
                
                rules={[{ required: true, message: 'Please enter your name' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="Your full name" disabled={true}/>
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Invalid email format' },
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="Your email" disabled={true}/>
              </Form.Item>

              <Form.Item
                name="mobile"
                label="Mobile Number"
                rules={[
                  { required: true, message: 'Please enter mobile number' },
                  { pattern: /^[0-9]{10}$/, message: 'Enter valid 10-digit number' },
                ]}
              >
                <Input prefix={<PhoneOutlined />} placeholder="9876543210" maxLength={10} disabled={true}/>
              </Form.Item>

              {/* <Divider>Change Password (Optional)</Divider>

              <Form.Item name="password" label="New Password">
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Leave blank to keep current password"
                />
              </Form.Item> */}

              {/* <div style={{ textAlign: 'right', marginTop: 40 }}>
                <Button type="primary" htmlType="submit" loading={saving} size="large">
                  Save Changes
                </Button>
              </div> */}
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default StaffProfile;